/**
 * P36 (Feb 28, 2026) — Quick-stats row for the unified Billing Hub.
 *
 * Renders 5 stat tiles: Drafts, Pending, Awaiting Response, Denials,
 * Revenue MTD. Each tile is a clickable card that deep-links into the
 * Claims list pre-filtered by the matching tab/status.
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { FileDashed, Clock, PaperPlaneTilt, WarningCircle, CurrencyDollar } from '@phosphor-icons/react';

const fmtMoney = (n) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n || 0);

const TILES = [
  {
    key: 'drafts',
    label: 'Drafts',
    icon: FileDashed,
    color: 'bg-slate-50 text-slate-700 border-slate-200',
    iconBg: 'bg-slate-100 text-slate-600',
    to: '/claims?tab=drafts',
    testid: 'hub-stat-drafts',
    hint: 'Unsubmitted work-in-progress',
  },
  {
    key: 'pending',
    label: 'Pending',
    icon: Clock,
    color: 'bg-amber-50 text-amber-800 border-amber-200',
    iconBg: 'bg-amber-100 text-amber-700',
    to: '/claims?status=Pending',
    testid: 'hub-stat-pending',
    hint: 'Queued for next batch submission',
  },
  {
    key: 'awaiting_response',
    label: 'Awaiting Response',
    icon: PaperPlaneTilt,
    color: 'bg-indigo-50 text-indigo-800 border-indigo-200',
    iconBg: 'bg-indigo-100 text-indigo-700',
    to: '/claims?tab=issues',
    testid: 'hub-stat-awaiting',
    hint: 'Sent to ODM, awaiting adjudication',
  },
  {
    key: 'denials',
    label: 'Denials',
    icon: WarningCircle,
    color: 'bg-rose-50 text-rose-800 border-rose-200',
    iconBg: 'bg-rose-100 text-rose-700',
    to: '/claims/denials',
    testid: 'hub-stat-denials',
    hint: 'Rejected or denied — needs rework',
  },
];

const HubStatsRow = ({ stats, loading }) => {
  const navigate = useNavigate();
  const display = (key) => (loading ? '—' : (stats?.[key] ?? 0));

  return (
    <div
      className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4"
      data-testid="hub-stats-row"
    >
      {TILES.map((tile) => {
        const Icon = tile.icon;
        return (
          <button
            key={tile.key}
            onClick={() => navigate(tile.to)}
            className={`text-left p-4 rounded-xl border ${tile.color} hover:shadow-md transition-shadow`}
            data-testid={tile.testid}
            title={tile.hint}
          >
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${tile.iconBg} mb-3`}>
              <Icon size={18} weight="fill" />
            </div>
            <div className="text-xs uppercase tracking-wide opacity-70">{tile.label}</div>
            <div className="text-2xl font-bold mt-1" data-testid={`${tile.testid}-value`}>
              {display(tile.key)}
            </div>
            <div className="text-[11px] opacity-60 mt-1 line-clamp-1">{tile.hint}</div>
          </button>
        );
      })}

      {/* Revenue MTD — separate styling because the value is money, not a count */}
      <button
        onClick={() => navigate('/reports')}
        className="text-left p-4 rounded-xl border bg-emerald-50 text-emerald-800 border-emerald-200 hover:shadow-md transition-shadow"
        data-testid="hub-stat-revenue-mtd"
        title="Payments posted (ERA) this calendar month"
      >
        <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-emerald-100 text-emerald-700 mb-3">
          <CurrencyDollar size={18} weight="fill" />
        </div>
        <div className="text-xs uppercase tracking-wide opacity-70">Revenue MTD</div>
        <div className="text-2xl font-bold mt-1" data-testid="hub-stat-revenue-mtd-value">
          {loading ? '—' : fmtMoney(stats?.revenue_mtd)}
        </div>
        <div className="text-[11px] opacity-60 mt-1 line-clamp-1">
          {loading ? '' : `${stats?.revenue_mtd_count || 0} claims paid`}
        </div>
      </button>
    </div>
  );
};

export default HubStatsRow;
